import { Node as CraftNode } from '@candulabs/craft/dist/core/nodes/types';
import { css, PlayCircleIcon, styled } from 'canoe';
import React, { ReactElement } from 'react';

import { FloatingLabel } from '@candu-ai/craft/ui';
import Node from 'craft/components/Node';
import { BuiltInComponents } from 'craft/constants';
import { useCss, useNodeContext } from 'craft/hooks';
import { useFlow, useFlowSelectionManager } from 'craft/state';
import { Resolver, ToolboxComponentSections } from 'craft/types';

import { flowInsertNodeTreesAtIndex } from './mutations';

interface Props {
  children?: ReactElement | ReactElement[];
}

const FlowNode = styled(({ hasFocus, ...props }) => <Node {...props} />)`
  ${({ theme, hasFocus }) => css`
    position: relative;
    ${hasFocus &&
    css`
      outline: 2px dashed ${theme.colors.BrandPurple} !important;

      &:hover {
        outline: 2px dashed ${theme.colors.BrandPurpleL10} !important;
      }
    `}
  `}
`;

export const Flow: Resolver = ({ children }: Props) => {
  const { id } = useNodeContext();
  const { index } = useFlow(id);
  const { css: cssObj } = useCss([id]);
  const { flexGrow, alignSelf, flexBasis, flexShrink } = cssObj ?? {};

  const { isFlowDescendantSelected, isFlowStepSelected } = useFlowSelectionManager(id);
  const showLabel = isFlowDescendantSelected && !isFlowStepSelected;

  return (
    <>
      <FlowNode
        overrideCss={{ flexGrow, alignSelf, flexBasis, flexShrink }}
        applyNodeProps={false}
        hasFocus={showLabel}
      >
        {showLabel && <FloatingLabel icon={PlayCircleIcon} label="Flow" />}
        {/**
         * SUUPER sketchy. this is truly, truly, ugly. figure out
         * how to do it safely
         @ts-ignore */}
        {children.props.children[index]}
      </FlowNode>
    </>
  );
};

Flow.craft = {
  name: 'Flow',
  isCanvas: true,
  rules: {
    canDrop(dropTarget, self, helpers): boolean {
      // We can't drop a flow inside another flow, no matter how nested
      const nodeApi = helpers(dropTarget.id);
      const ancestors = nodeApi.ancestors(true);

      if (dropTarget.data.name === BuiltInComponents.Flow) {
        return false;
      }

      if (
        ancestors.find((ancestor) => nodeApi.nodes[ancestor].data.name === BuiltInComponents.Flow)
      ) {
        return false;
      }

      return true;
    },
    canMoveIn: (incomingNodes: CraftNode[]) =>
      incomingNodes.every((node) => node.data.name === BuiltInComponents.FlowStep),
  },
};

Flow.mutations = {
  insertNodeTreesAtIndex: flowInsertNodeTreesAtIndex,
};

Flow.toolbox = {
  schema: {
    [ToolboxComponentSections.Layout]: {
      blockedDisplays: ['inline-block', 'flex'],
    },
  },
};
